import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import NotificationBell from './NotificationBell';
import RecentViewsDropdown from './RecentViewsDropdown';
import SyncStatusIndicator from './SyncStatusIndicator';import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useTranslation, LANGUAGES } from '../i18n';
import {
  Search,
  Download,
  User,
  LogOut,
  Settings,
  Maximize2,
  FileDown,
  Network,
  BookOpen,
  Cable,
  Layers,
  GitCompareArrows,
  BarChart3,
  Home,
  Moon,
  Sun,
  Globe,
  Brain,
  ArrowLeftRight,
  Wand2,
  DollarSign,
  Wrench,
  Crown,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

export default function Header({
  properties,
  selectedProperty,
  onPropertyChange,
  searchQuery,
  onSearch,
  onFitToView,
  role,
  onAdmin,
  onLogout,
  stackData,
  graphRef,
  selectedNode,
  activeTab,
  onTabChange,
  editMode,
  onToggleEditMode,
  activeLayer,
  onLayerChange,
  isMobile,
  compareMode,
  onToggleCompare,
  onOpenSearch,
  darkMode,
  onToggleDarkMode,
  tenantSwitcher,
  onGoToSite,
  hasPermission,
  whiteLabel,
  isSuperAdmin,
  token,
  onJumpRecentView,
  onCreateSnapshot,
}) {
  const { t, lang, setLang } = useTranslation();
  const [exporting, setExporting] = useState(false);

  const getPropertyLabel = () => {
    if (selectedProperty === 'overall') return 'Overall Map';
    const prop = properties.find(p => p.id === selectedProperty);
    return prop?.name || selectedProperty;
  };

  const exportPdf = async (mode = 'as-viewed') => {
    if (!graphRef?.current?.getSvgElement) return;
    setExporting(true);
    try {
      const svgEl = graphRef.current.getSvgElement();
      if (!svgEl) return;

      const container = svgEl.closest('.graph-container');
      if (!container) return;

      const dataUrl = await toPng(container, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#F7FAFC',
        filter: (node) => {
          if (node.classList && node.classList.contains('graph-controls-overlay')) return false;
          return true;
        }
      });

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add title
      pdf.setFontSize(14);
      pdf.setTextColor(30, 41, 59);
      const title = `${whiteLabel?.platform_name || 'LodgiFlow'} — ${getPropertyLabel()} Tech Stack`;
      pdf.text(title, 10, 12);

      // Add stats
      if (stackData) {
        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`${stackData.used_platforms} of ${stackData.total_platforms} platforms active`, 10, 17);
      }

      // Add graph image
      const img = new Image();
      img.src = dataUrl;
      await new Promise((resolve) => { img.onload = resolve; });

      const imgAspect = img.width / img.height;
      const availWidth = pageWidth - 20;
      const availHeight = pageHeight - 40;
      let drawW, drawH;

      if (mode === 'fit-to-page') {
        if (imgAspect > availWidth / availHeight) {
          drawW = availWidth;
          drawH = availWidth / imgAspect;
        } else {
          drawH = availHeight;
          drawW = availHeight * imgAspect;
        }
      } else {
        drawW = Math.min(availWidth, img.width * 0.264583);
        drawH = drawW / imgAspect;
        if (drawH > availHeight) {
          drawH = availHeight;
          drawW = drawH * imgAspect;
        }
      }

      const x = (pageWidth - drawW) / 2;
      pdf.addImage(dataUrl, 'PNG', x, 22, drawW, drawH);

      // Add legend at bottom
      const legendY = pageHeight - 15;
      pdf.setFontSize(7);
      pdf.setTextColor(100, 116, 139);
      const categories = stackData?.categories || [];
      let legendX = 10;
      categories.forEach((cat) => {
        pdf.setFillColor(cat.color);
        pdf.circle(legendX + 2, legendY, 1.5, 'F');
        pdf.text(cat.name, legendX + 5, legendY + 0.8);
        legendX += pdf.getTextWidth(cat.name) + 10;
        if (legendX > pageWidth - 40) {
          legendX = 10;
        }
      });

      // Add footer line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(10, legendY - 4, pageWidth - 10, legendY - 4);

      // Legend for line types
      pdf.setFontSize(6);
      const lineY = legendY + 5;
      pdf.text('━━ Two-way integration', 10, lineY);
      pdf.text('──▸ One-way integration', 60, lineY);
      pdf.text('○ Unused platform (greyed)', 110, lineY);

      const dateStr = new Date().toLocaleDateString();
      pdf.text(`Generated: ${dateStr}`, pageWidth - 35, lineY);

      pdf.save(`innisfree-tech-stack-${selectedProperty}-${mode}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <header className="shrink-0 z-30 no-print border-b bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 lf-chrome" data-focus-hide="true" style={whiteLabel?.header_bg ? { backgroundColor: whiteLabel.header_bg } : undefined}>
      {/* Row 1: Brand + Tabs + Actions */}
      <div className="h-12 md:h-14 px-3 md:px-6 flex items-center justify-between gap-2">
        {/* Left: Brand + Page Tabs */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0 overflow-hidden">
          <button onClick={() => onTabChange('home')} className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity cursor-pointer" data-testid="brand-home">
            <img src={whiteLabel?.logo_url || '/assets/logo.png'} alt={whiteLabel?.platform_name || 'LodgiFlow'} className="h-7 w-7" />
            {!(whiteLabel?.hide_lodgiflow_branding) && (
              <h1 className="text-base md:text-lg font-bold tracking-tight whitespace-nowrap" style={{ fontFamily: 'Space Grotesk, sans-serif', color: whiteLabel?.primary_color || undefined }}>
                {whiteLabel?.platform_name || 'LodgiFlow'}
              </h1>
            )}
          </button>
          <Separator orientation="vertical" className="h-5 hidden lg:block shrink-0" />
          {tenantSwitcher && <div className="hidden md:block shrink-0">{tenantSwitcher}</div>}
          {!!isSuperAdmin && <SyncStatusIndicator token={token} />}
          <div className="hidden md:flex items-center gap-0.5 bg-muted/60 rounded-lg p-0.5 overflow-x-auto scrollbar-none shrink-0">
            {[
              { id: 'home', icon: Home, label: t('nav.home'), perm: 'nav.home', show: true },
              { id: 'visualizer', icon: Network, label: t('nav.stackMap'), mobileLabel: 'Map', perm: 'nav.stack_map', show: true },
              { id: 'database', icon: BookOpen, label: t('nav.techDatabase'), mobileLabel: 'DB', perm: 'nav.tech_database', show: true },
              { id: 'reports', icon: BarChart3, label: t('nav.reports'), perm: 'nav.reports', show: true },
              { id: 'ai', icon: Brain, label: t('nav.ai'), perm: 'nav.ai', show: hasPermission ? hasPermission('nav.ai') : role === 'admin' },
              { id: 'tools', icon: Wrench, label: 'Tools', perm: 'nav.admin', show: hasPermission ? hasPermission('nav.admin') : role === 'admin' },
              { id: 'admin', icon: Settings, label: t('nav.admin'), perm: 'nav.admin', show: hasPermission ? hasPermission('nav.admin') : role === 'admin' },
            ].filter(t => t.show).map(t => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => onTabChange(t.id)}
                className={`flex items-center gap-1 px-2 lg:px-3 py-1 lg:py-1.5 rounded-md text-[11px] lg:text-xs font-medium transition-colors whitespace-nowrap shrink-0 ${
                  activeTab === t.id
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <t.icon className="h-3 w-3 lg:h-3.5 lg:w-3.5" /> <span className="hidden md:inline">{isMobile ? (t.mobileLabel || t.label) : t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
          {/* Global Admin - separate from main nav */}
          {!!isSuperAdmin && (
            <button
              data-testid="tab-super-admin"
              onClick={() => onTabChange('super-admin')}
              className={`flex items-center gap-1 px-2 lg:px-3 py-1 lg:py-1.5 rounded-md text-[11px] lg:text-xs font-medium transition-colors whitespace-nowrap shrink-0 border ${
                activeTab === 'super-admin'
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : 'text-purple-500 border-purple-200 hover:bg-purple-50 hover:text-purple-700'
              }`}
            >
              <Crown className="h-3 w-3 lg:h-3.5 lg:w-3.5" /> <span className="hidden md:inline">Global Admin</span>
            </button>
          )}
          {/* Global Search */}
          {onOpenSearch && (
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground" onClick={onOpenSearch} data-testid="global-search-button">
              <Search className="h-3.5 w-3.5" />
              {!isMobile && <span>Search</span>}
              {!isMobile && <kbd className="text-[9px] bg-muted px-1 py-0.5 rounded font-mono ml-1">⌘K</kbd>}
            </Button>
          )}

          {/* Notification Bell */}
          {token && <NotificationBell token={token} />}

          {activeTab === 'visualizer' && !isMobile && (
          <>
        {hasPermission && hasPermission('action.edit_integrations') && (
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            data-testid="edit-integrations-toggle"
            onClick={onToggleEditMode}
            className="gap-1.5 hidden lg:flex"
          >
            <Cable className="h-3.5 w-3.5" />
            {editMode ? 'Exit Edit' : 'Edit Integrations'}
          </Button>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 hidden md:flex" onClick={onFitToView}>
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fit to view</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="export-pdf-button"
              disabled={exporting}
              className="gap-1.5 hidden md:flex"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => exportPdf('as-viewed')}
              data-testid="export-pdf-as-viewed-button"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export PDF (As Viewed)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => exportPdf('fit-to-page')}
              data-testid="export-pdf-fit-to-page-button"
            >
              <Maximize2 className="h-4 w-4 mr-2" />
              Export PDF (Fit to Page)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
          </>
        )}

        <Separator orientation="vertical" className="h-5 hidden md:block" />

        {/* Language selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1 hidden md:flex" data-testid="language-selector">
              {LANGUAGES.find(l => l.code === lang)?.flag || '🌐'} {lang.toUpperCase()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {LANGUAGES.map(l => (
              <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? 'bg-muted' : ''}>
                <span className="mr-2">{l.flag}</span> {l.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Accessibility: font size + invert */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hidden md:flex" data-testid="accessibility-menu">
              <span className="text-sm font-bold">Aa</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase">Display</div>
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-xs">Text Size</span>
              <div className="flex gap-0.5">
                <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => {
                  const s = parseFloat(document.documentElement.style.fontSize || '100') - 10;
                  document.documentElement.style.fontSize = Math.max(80, s) + '%';
                }}>A-</Button>
                <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => {
                  document.documentElement.style.fontSize = '100%';
                }}>A</Button>
                <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => {
                  const s = parseFloat(document.documentElement.style.fontSize || '100') + 10;
                  document.documentElement.style.fontSize = Math.min(140, s) + '%';
                }}>A+</Button>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => document.documentElement.classList.toggle('invert')}>
              <span className="mr-2">◐</span> Invert Colors
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => document.documentElement.classList.toggle('grayscale')}>
              <span className="mr-2">◑</span> Grayscale
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="user-menu">
              <User className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1.5">
              <p className="text-xs text-muted-foreground">Signed in as</p>
              <p className="text-sm font-medium capitalize">{role}</p>
            </div>
            <DropdownMenuSeparator />
            {onToggleDarkMode && (
              <DropdownMenuItem onClick={onToggleDarkMode}>
                {darkMode ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
            )}
            {onGoToSite && (
              <DropdownMenuItem onClick={onGoToSite}>
                <Globe className="h-4 w-4 mr-2" />
                LodgiFlow Website
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Row 2: Mobile controls bar (visualizer tab only) */}
      {activeTab === 'visualizer' && (
        <div className="px-3 md:px-6 py-2 flex items-center gap-2 border-t md:border-t-0 overflow-x-auto">
          <Select
            value={selectedProperty}
            onValueChange={onPropertyChange}
            data-testid="property-select"
          >
            <SelectTrigger className={`${isMobile ? 'w-[180px]' : 'w-[260px]'} h-8 md:h-9 text-xs md:text-sm`} data-testid="property-select">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overall">Overall Map (All Platforms)</SelectItem>
              {properties.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Recent Views quick-jump */}
          {token && <RecentViewsDropdown token={token} onJump={onJumpRecentView} size={isMobile ? 'sm' : 'md'} />}

          {/* Share this view — dropdown menu with live-link and snapshot options */}
          <DropdownMenu>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className={`${isMobile ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'} shrink-0`}
                      data-testid="share-view-btn"
                      aria-label="Share this view"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">Share this view</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Live deep-link copied — teammates land on this exact property + node');
                  } catch { toast.error('Could not copy — grab it from the address bar'); }
                }}
                data-testid="share-copy-link"
              >
                <Cable className="h-4 w-4 mr-2 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm">Copy live link</div>
                  <div className="text-[10px] text-muted-foreground">Requires the recipient to log in</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onCreateSnapshot && onCreateSnapshot()}
                data-testid="share-create-snapshot"
              >
                <Wand2 className="h-4 w-4 mr-2 text-indigo-500" />
                <div className="flex-1">
                  <div className="text-sm">Create snapshot link</div>
                  <div className="text-[10px] text-muted-foreground">Read-only, no login required — great for demos</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {!isMobile && (
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="platform-search-input"
                className="pl-9 h-8 md:h-9"
                placeholder="Search platforms..."
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
          )}

          {/* Layer toggle */}
          {selectedProperty && selectedProperty !== 'overall' && onLayerChange && (
            <div className="flex items-center gap-0.5 bg-muted/60 rounded-lg p-0.5 shrink-0" data-testid="layer-toggle">
              {[
                { value: 'actual', label: 'Actual' },
                { value: 'possible', label: 'Possible' },
                { value: 'ideal', label: 'Ideal' },
              ].map(layer => (
                <button
                  key={layer.value}
                  data-testid={`layer-${layer.value}`}
                  onClick={() => onLayerChange(layer.value)}
                  className={`px-2 md:px-2.5 py-1 rounded-md text-[10px] md:text-[11px] font-medium transition-colors whitespace-nowrap ${
                    activeLayer === layer.value
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {layer.label}
                </button>
              ))}
            </div>
          )}

          {/* Compare toggle */}
          {selectedProperty && selectedProperty !== 'overall' && onToggleCompare && (
            <Button
              variant={compareMode ? 'default' : 'outline'}
              size="sm"
              data-testid="compare-toggle"
              onClick={onToggleCompare}
              className={`gap-1.5 text-[10px] md:text-xs shrink-0 ${compareMode ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            >
              <GitCompareArrows className="h-3 w-3 md:h-3.5 md:w-3.5" />
              {isMobile ? 'Gap' : 'Quick Compare'}
            </Button>
          )}
        </div>
      )}
    </header>
  );
}
